/**
 * „Wszystkie ćwiczenia": every movement across the client's last five ISO weeks, a cell per week
 * holding that week's top set in the movement's own fields, and the four-week change on the number
 * the backend reads the movement by (`primaryChange4w`: e1RM for a lift, the longest single stretch
 * for a run, the longest set for a plank). That number is always a best set, so it is labelled as
 * one and never with Forma's session sums. A week the movement was not lifted is `null`, and a row shorter than
 * the week list is padded with empty weeks rather than shifted left.
 */

import {
  lineWords,
  liftMetric,
  metricLine,
  setInFields,
  type LiftFields,
  type LiftMetric,
  type MeasuredSet,
} from '~/entities/lift-history';
import type { Translate } from '~/shared/lib/localeTranslate';

export interface MatrixTopSetSource extends MeasuredSet {
  readonly e1rmKg: number | null;
}

export interface MatrixChangeSource {
  /** The backend's `LiftMetric` name: `E1rm`, `Weight`, `Distance`, `Reps`, `Duration`, … */
  readonly metric: string;
  /** An absolute difference in the metric's wire unit: kilograms, metres, seconds, repetitions. */
  readonly value: number;
}

export interface MatrixRowSource {
  readonly exerciseId: number;
  readonly name: string;
  /** `null` from a backend older than the field list: the row reads as weight × reps. */
  readonly fields: LiftFields;
  readonly cells: readonly (MatrixTopSetSource | null)[];
  readonly change: MatrixChangeSource | null;
  /** The older backend's only change, read when `change` is absent. */
  readonly e1rmChange4wKg: number | null;
}

export interface MatrixSource {
  /** Each week's Monday, as the reader's local midnight of that civil date, oldest first. */
  readonly weeks: readonly number[];
  readonly rows: readonly MatrixRowSource[];
  /** More movements were lifted in the window than the rows carry. */
  readonly truncated: boolean;
}

export interface MatrixCell {
  readonly top: string;
  readonly sub: string | null;
}

export interface MatrixRowView {
  readonly exerciseId: number;
  readonly name: string;
  readonly unread: boolean;
  /** One per week; `null` is a week the movement was not lifted. */
  readonly cells: readonly (MatrixCell | null)[];
  readonly change: string | null;
  /** The best set the change is measured on („e1RM", „Najdłuższy odcinek"), or `null` with no change. */
  readonly changeOf: string | null;
  readonly changeIsGood: boolean;
}

export interface MatrixView {
  readonly weeks: readonly string[];
  readonly rows: readonly MatrixRowView[];
  /** Said under the matrix when the server cut the list short; `null` when it is whole. */
  readonly limitNote: string | null;
}

export function buildMatrix(
  source: MatrixSource,
  unread: ReadonlySet<number>,
  { t, locale }: { readonly t: Translate; readonly locale: string },
): MatrixView {
  const one = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  const dayMonth = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });

  const words = { t, locale };
  const cellOf = (
    cell: MatrixTopSetSource | null | undefined,
    fields: LiftFields,
  ): MatrixCell | null => {
    const top = cell == null ? null : setInFields(cell, fields, words);
    if (cell == null || top === null) return null;
    return {
      top,
      sub: cell.e1rmKg === null ? null : t('web:forma.matrix.e1rm', { value: one.format(cell.e1rmKg) }),
    };
  };

  return {
    weeks: source.weeks.map((monday) => dayMonth.format(new Date(monday))),
    rows: source.rows.map((row) => {
      const change = changeOf(row);
      // Sized by the row's own cells, so the change of a run read in kilometres is in kilometres too.
      const largest = Math.max(
        Math.abs(change?.value ?? 0),
        ...row.cells.map((cell) => cell?.distanceMeters ?? 0),
      );
      const changeWords = change === null ? null : lineWords(metricLine(change.metric), words, largest);
      return {
        exerciseId: row.exerciseId,
        name: row.name,
        unread: unread.has(row.exerciseId),
        cells: source.weeks.map((_, index) => cellOf(row.cells[index], row.fields)),
        change: change === null || changeWords === null ? null : changeWords.signedWithUnit(change.value),
        changeOf: change === null ? null : t(`web:forma.matrix.metric.${change.metric}`),
        changeIsGood: change !== null && changeWords !== null && changeWords.isImprovement(change.value),
      };
    }),
    limitNote: source.truncated ? t('web:forma.matrix.truncated', { count: source.rows.length }) : null,
  };
}

/** The primary change when the backend names one it can be written in, else the e1RM change. */
function changeOf(row: MatrixRowSource): { readonly metric: LiftMetric; readonly value: number } | null {
  const metric = row.change === null ? null : liftMetric(row.change.metric);
  if (metric !== null && row.change !== null) return { metric, value: row.change.value };
  return row.e1rmChange4wKg === null ? null : { metric: 'e1rm', value: row.e1rmChange4wKg };
}
